import { create } from 'zustand';
import { supabase } from './supabase';

export type Role = 'owner' | 'manager' | 'staff';

export type LocationKind = 'storefront' | 'warehouse' | 'portable';

export interface LocationOption {
  id: string;
  name: string;
  kind: LocationKind;
  is_default: boolean;
}

export type SessionStatus = 'loading' | 'unauthenticated' | 'authenticated';

interface SessionState {
  status: SessionStatus;

  // ----- user identity (auth.users + public.users) ---------------------------
  userId: string | null;
  email: string | null;
  displayName: string | null;

  // ----- the user's single shop + role -------------------------------------
  // Locked Epic 0.X: one user = one shop. `role` is the user's role in that
  // shop. Multiple owners per shop is fine (different user accounts); one
  // user cannot belong to two shops.
  currentShopId: string | null;
  shopName: string | null;
  role: Role | null;

  // ----- locations the user can operate from inside that shop ---------------
  availableLocations: LocationOption[];
  currentLocationId: string | null;

  // ----- active bin for scan-to-add (defaults to null = use Processing bin) -
  activeBinId: string | null;

  // ----- actions ------------------------------------------------------------
  /** Bulk-set the session — called by sessionBootstrap on auth state changes. */
  setSession: (s: Partial<SessionState>) => void;
  /** Persist a new current location to users.current_location_id + update store. */
  switchLocation: (locationId: string) => Promise<void>;
  /** Set the active bin for scan-to-add. null = use the location's Processing bin. */
  setActiveBin: (id: string | null) => void;
  /** Reset to unauthenticated and let RequireAuth redirect. */
  clear: () => void;
  /** Sign the user out of Supabase and reset the store. */
  signOut: () => Promise<void>;
}

const initialState: Pick<
  SessionState,
  | 'status'
  | 'userId'
  | 'email'
  | 'displayName'
  | 'currentShopId'
  | 'shopName'
  | 'role'
  | 'availableLocations'
  | 'currentLocationId'
  | 'activeBinId'
> = {
  status: 'loading',
  userId: null,
  email: null,
  displayName: null,
  currentShopId: null,
  shopName: null,
  role: null,
  availableLocations: [],
  currentLocationId: null,
  activeBinId: null,
};

/**
 * Global auth + shop + location-context store. Populated by `sessionBootstrap`
 * once Supabase resolves the user's session; consumed by `RequireAuth`,
 * `DesktopLayout` (avatar / location switcher), and feature pages that need
 * `currentShopId` (for RLS-scoped queries) or `currentLocationId` (for
 * inventory operations defaulting to the active location).
 */
export const useSession = create<SessionState>((set, get) => ({
  ...initialState,

  setSession: (s) => set(s),

  switchLocation: async (locationId) => {
    const userId = get().userId;
    if (!userId) return;
    if (!get().availableLocations.some((l) => l.id === locationId)) return;
    set({ currentLocationId: locationId, activeBinId: null });
    const { error } = await supabase
      .from('users')
      .update({ current_location_id: locationId })
      .eq('id', userId);
    if (error) {
      console.warn('[storefront] failed to persist current_location_id', error.message);
    }
  },

  setActiveBin: (id) => set({ activeBinId: id }),

  clear: () => set({ ...initialState, status: 'unauthenticated' }),

  signOut: async () => {
    await supabase.auth.signOut();
    set({ ...initialState, status: 'unauthenticated' });
  },
}));

interface UIState {
  navbarCollapsed: boolean;
  toggleNavbar: () => void;
}

export const useUI = create<UIState>((set) => ({
  navbarCollapsed: false,
  toggleNavbar: () => set((s) => ({ navbarCollapsed: !s.navbarCollapsed })),
}));
